import { EMPTY_EDITORS_COLUMN } from '../constants/reentry-planning.constants';

/**
 * Display name for an editor from list or GET /editors payloads.
 * @param {object} [editor]
 * @returns {string}
 */
export const formatEditorDisplayName = (editor) => {
  if (!editor) {
    return '';
  }

  if (editor.fullName) {
    return editor.fullName;
  }

  if (editor.name) {
    return editor.name;
  }

  return `${editor.firstName ?? ''} ${editor.lastName ?? ''}`.trim();
};

/**
 * Comma-separated editor names for the My/All Plans grid. Empty / orphan → em dash.
 * @param {Array<object>} [editors]
 * @returns {string}
 */
export const formatEditorsColumn = (editors) => {
  if (!Array.isArray(editors) || editors.length === 0) {
    return EMPTY_EDITORS_COLUMN;
  }

  const names = editors.map(formatEditorDisplayName).filter(Boolean);
  return names.length ? names.join(', ') : EMPTY_EDITORS_COLUMN;
};

const toPersonId = (value) => {
  const personId = Number(value);
  return Number.isFinite(personId) && personId > 0 ? personId : null;
};

/**
 * Maps a GET /editors member onto the EditorsSection chip shape.
 * `isSelf` is set only for people already on the active list (so a locally
 * self-added chip can still be removed before Save).
 * @param {object} editor
 * @param {number|string} [currentPersonId]
 * @returns {object|null}
 */
export const mapActiveEditorToChip = (editor, currentPersonId) => {
  if (!editor || editor.personId == null) {
    return null;
  }

  const personId = toPersonId(editor.personId);
  const currentId = toPersonId(currentPersonId);

  return {
    personId: personId ?? editor.personId,
    name: formatEditorDisplayName(editor),
    hasEditPermission: editor.hasEditPermission !== false,
    isSelf: personId != null && currentId != null && personId === currentId,
  };
};

/**
 * @param {Array<object>} [editors]
 * @returns {number[]}
 */
export const mapEditorsToIds = (editors = []) =>
  editors.map((editor) => toPersonId(editor?.personId)).filter((personId) => personId != null);

/**
 * Editors button / manage gate. Preview never shows the button. Do not key off view_any_detail.
 * @param {{ isEditor?: boolean, hasAddEditorPermission?: boolean, isPreviewPlan?: boolean }} params
 * @returns {boolean}
 */
export const canManagePlanEditors = ({
  isEditor = false,
  hasAddEditorPermission = false,
  isPreviewPlan = false,
} = {}) =>
  isPreviewPlan !== true && (isEditor === true || hasAddEditorPermission === true);
